import type { MouseEvent, RefObject, TouchEvent } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import throttle from 'lodash/throttle';
import { useEffect, useRef, useState } from 'react';
import useEvent from 'react-use/lib/useEvent';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import { DEFAULT_THEME, INIT_TIME } from '../config';
import { useHandlers } from '../helpers/get-handlers';
import { DEFAULT_BROWSER } from '../../../config';
import type { IHeaderPrivateProps, IHeaderPublicProps } from '../../../../models';
import type { ITitle, TThemes } from '../../../tabs/models';
import type { TPanelHeaderMobileTheme } from '../../panel-header/models';
import type { TTabHeadersTheme } from '../models';

type TThemeStyles = TTabHeadersTheme & TPanelHeaderMobileTheme;

export interface IState {
    linePosition: number;
    lineWidth: number;
    selectedID: number | string;
    translateX: number;
}
export type TUseTabsHeaderProps = Pick<IHeaderPrivateProps, 'onChange'> &
    Pick<IHeaderPublicProps, 'titles' | 'align' | 'browser' | 'selectedID'> & {
        theme: TThemes;
    };

export type TUseTabHeaderResult = {
    alignStyle?: string;
    headerContainer: RefObject<HTMLDivElement>;
    headerList: RefObject<HTMLDivElement>;
    linePosition: number;
    lineWidth: number;
    onClick: (
        event: MouseEvent<HTMLDivElement>,
        id: number | string,
        dataLayer?:
            | {
                  [key: string]: string;
              }
            | undefined
    ) => void;
    onTouchEnd: (event: TouchEvent<Element>) => void;
    onTouchStart: (event: TouchEvent<Element>) => void;
    selectedTab: RefObject<HTMLDivElement>;
    themeStyle: TThemeStyles;
    translateX: number;
    withIcon?: boolean;
};

export type TUseTabsHeader = (props: TUseTabsHeaderProps) => TUseTabHeaderResult;

export const useTabsHeader: TUseTabsHeader = ({
    align,
    browser = DEFAULT_BROWSER,
    onChange,
    selectedID,
    theme = DEFAULT_THEME,
    titles,
}) => {
    const [{ id: titleFirstId }] = titles;

    const withIcon = !!titles.find((item: ITitle) => item.iconSrc);

    const headerContainer = useRef<HTMLDivElement>(null);

    const headerList = useRef<HTMLDivElement>(null);

    const selectedTab = useRef<HTMLDivElement>(null);

    const start = useRef(false);

    const move = useRef(false);

    const isScrollable = useRef(false);

    const coords = useRef({
        x: 0,
        y: 0,
    });

    const headerContainerWidth = useRef(0);

    const minxtranslateX = useRef(0);

    const [state, setState] = useState<IState>({
        translateX: 0,
        lineWidth: 0,
        linePosition: 0,
        selectedID: selectedID || selectedID === 0 ? selectedID : titleFirstId,
    });

    const { changePositionToSelectedID, init, onTouchEnd, onTouchStart } = useHandlers({
        browser,
        coords,
        headerList,
        headerContainer,
        headerContainerWidth,
        isScrollable,
        minxtranslateX,
        move,
        selectedID: selectedID ?? titleFirstId,
        selectedTab,
        setState,
        start,
        state,
        titles,
    });

    /** Если приходит новый список заголовков, делаем подскролл */
    useUpdateEffect(() => {
        if (selectedID) {
            changePositionToSelectedID(selectedID);
        }
    }, [titles.length, headerContainerWidth]);

    useEffect(() => {
        setTimeout(init, INIT_TIME);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedID && selectedID !== state.selectedID) {
            changePositionToSelectedID(selectedID, state.selectedID);
        }
    }, [changePositionToSelectedID, selectedID, state.selectedID]);

    const resize = throttle(init);

    useEvent('resize', resize);

    const onClick = (
        event: MouseEvent<HTMLDivElement>,
        id: number | string,
        dataLayer?: { [key: string]: string }
    ) => {
        if (dataLayer) {
            sendEvent(dataLayer);
        }

        onChange({ event, id });
    };

    const { linePosition, lineWidth, translateX } = state;

    const themeStyle = `${theme}Theme` as TThemeStyles;
    const alignStyle = `${align}Align`;

    return {
        alignStyle,
        headerContainer,
        headerList,
        linePosition,
        lineWidth,
        onClick,
        onTouchEnd,
        onTouchStart,
        selectedTab,
        translateX,
        themeStyle,
        withIcon,
    };
};
